import React, { useEffect, useState } from 'react';
import { Search, UserPlus, Mail, X, Check, Clock, Users2, Loader2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { toast } from 'sonner';

/**
 * Tax Specialist page — request to become a member's dedicated specialist.
 *
 * Lives at /admin/member-requests. Combines:
 *  - Eligible members search (members the TS has worked with + name/email search).
 *  - Compose offer modal (personal message).
 *  - Outgoing offers list (pending / accepted / declined / withdrawn).
 */
export default function SpecialistMemberRequestsPage() {
  const [members, setMembers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [composeFor, setComposeFor] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [mem, off] = await Promise.all([
        api.specialistEligibleMembers(q.trim() || undefined),
        api.specialistListOffers(),
      ]);
      setMembers(mem || []);
      setOffers(off || []);
    } catch {
      toast.error('Could not load members');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const sendOffer = async () => {
    if (!message.trim()) { toast.error('Add a short personal note'); return; }
    setSubmitting(true);
    try {
      await api.specialistOfferMember(composeFor.id, message);
      toast.success(`Offer sent to ${composeFor.name || composeFor.email}`);
      setComposeFor(null); setMessage('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send offer');
    } finally { setSubmitting(false); }
  };

  const withdraw = async (id) => {
    try {
      await api.specialistWithdrawOffer(id);
      toast.success('Offer withdrawn');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not withdraw');
    }
  };

  const pendingOffers = offers.filter((o) => o.status === 'pending');
  const recentOffers = offers.filter((o) => o.status !== 'pending').slice(0, 10);

  return (
    <AdminLayout title="Member requests" subtitle="Offer to become a member's dedicated Tax Specialist">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Eligible members */}
        <section className="lg:col-span-7 rounded-xl bg-white border border-neutral-100 p-6">
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <h2 className="text-[17px] font-bold text-[#14110f] flex items-center gap-2">
              <Users2 className="w-4 h-4 text-[#c89200]" />Find a member
            </h2>
            <form onSubmit={onSearch} className="flex items-stretch gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input data-testid="member-search"
                       value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Search by email or name…"
                       className="pl-9 pr-3 py-2 rounded-md border border-neutral-200 text-[13px] text-[#14110f] focus:border-[#f5b300] outline-none bg-white w-[260px]" />
              </div>
              <button type="submit" className="px-3 py-2 rounded-md bg-[#14110f] text-white text-[12.5px] font-semibold hover:bg-black">
                Search
              </button>
            </form>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-neutral-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-center text-[13px] text-neutral-500 py-10">
              No members match — try a different search term or keep building bookings to grow this list.
            </p>
          ) : (
            <ul data-testid="eligible-members" className="space-y-2">
              {members.map((m) => (
                <li key={m.id} data-testid={`member-row-${m.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-neutral-100 px-4 py-3 hover:border-[#f5b300] transition">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#14110f] text-[13.5px] truncate">{m.name || m.email}</p>
                    <p className="text-[11.5px] text-neutral-500 truncate">{m.email}</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {m.source === 'appointment' && (
                        <span className="text-[10.5px] uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-bold">Past appointment</span>
                      )}
                      {m.has_specialist && (
                        <span className="text-[10.5px] uppercase tracking-wider bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-bold">Already has TS</span>
                      )}
                      {m.has_pending_offer && (
                        <span className="text-[10.5px] uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-bold">Offer pending</span>
                      )}
                    </div>
                  </div>
                  <button data-testid={`offer-btn-${m.id}`}
                          disabled={m.has_pending_offer}
                          onClick={() => setComposeFor(m)}
                          className="shrink-0 inline-flex items-center gap-1.5 bg-[#f5b300] hover:bg-[#e0a200] disabled:bg-neutral-200 disabled:text-neutral-400 text-[#14110f] font-semibold px-3 py-1.5 rounded-md text-[12.5px]">
                    <UserPlus className="w-3.5 h-3.5" />Offer
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Outgoing offers */}
        <section className="lg:col-span-5 space-y-5">
          <div className="rounded-xl bg-white border border-neutral-100 p-6">
            <h3 className="text-[15px] font-bold text-[#14110f] flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-[#c89200]" />Pending offers ({pendingOffers.length})
            </h3>
            {pendingOffers.length === 0 ? (
              <p className="text-[12.5px] text-neutral-500 italic">No offers awaiting a response.</p>
            ) : (
              <ul data-testid="pending-offers" className="space-y-2">
                {pendingOffers.map((o) => (
                  <li key={o.id} className="rounded-md border border-neutral-100 px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-[#14110f] text-[13px] truncate">{o.member_name || o.member_email}</p>
                        <p className="text-[11.5px] text-neutral-500 truncate">{o.member_email}</p>
                      </div>
                      <button data-testid={`withdraw-${o.id}`}
                              onClick={() => withdraw(o.id)}
                              className="text-[11.5px] text-rose-600 hover:underline font-semibold inline-flex items-center gap-1">
                        <X className="w-3 h-3" />Withdraw
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl bg-white border border-neutral-100 p-6">
            <h3 className="text-[15px] font-bold text-[#14110f] mb-4">Recent activity</h3>
            {recentOffers.length === 0 ? (
              <p className="text-[12.5px] text-neutral-500 italic">No closed offers yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {recentOffers.map((o) => (
                  <li key={o.id} className="flex items-center gap-2 text-[12px]">
                    {o.status === 'accepted' ? (
                      <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    ) : (
                      <X className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                    )}
                    <span className="truncate font-semibold text-[#14110f]">{o.member_name || o.member_email}</span>
                    <span className="text-neutral-400">·</span>
                    <span className="text-neutral-500 capitalize">{o.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {composeFor && (
        <div data-testid="compose-offer-modal"
             className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
            <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
              <h3 className="font-bold text-[#14110f] flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#c89200]" />
                Offer to {composeFor.name || composeFor.email}
              </h3>
              <button onClick={() => { setComposeFor(null); setMessage(''); }}
                      className="text-neutral-400 hover:text-[#14110f]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {composeFor.has_specialist && (
                <div className="rounded-md bg-amber-50 border border-amber-100 px-3 py-2 text-[12.5px] text-amber-800">
                  This member already has a Tax Specialist. If they accept, the switch will need admin approval.
                </div>
              )}
              <label className="block">
                <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Personal note</span>
                <textarea data-testid="offer-message"
                          value={message} onChange={(e) => setMessage(e.target.value)}
                          rows={5}
                          placeholder="Why you'd love to work with them, your specialties, languages you speak…"
                          className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] text-[#14110f] placeholder:text-neutral-400 focus:border-[#f5b300] outline-none bg-white" />
              </label>
              <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-3">
                <button onClick={() => { setComposeFor(null); setMessage(''); }}
                        className="text-[13px] text-neutral-500 hover:text-[#14110f]">
                  Cancel
                </button>
                <button data-testid="send-offer-btn"
                        onClick={sendOffer} disabled={submitting}
                        className="inline-flex items-center gap-1.5 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold px-4 py-2 rounded-md text-[13px]">
                  <Mail className="w-3.5 h-3.5" />{submitting ? 'Sending…' : 'Send offer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
